import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import {
  Callout,
  ListEmptyState,
  LoadingSpinner,
  Select,
  TextInput,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerLookupItem } from '../../types/customer';
import { ShipmentOpenFilesReport } from '../../types/reports';
import { formatDate, formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportShipmentOpenFiles } from './exportShipmentReports';

export function ShipmentOpenFilesPage() {
  const labels = t().reports;
  const [customers, setCustomers] = useState<CustomerLookupItem[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [port, setPort] = useState('');
  const [report, setReport] = useState<ShipmentOpenFilesReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('daysOpen', 'desc');

  const rows = useMemo(
    () =>
      report
        ? sortItems(report.data, sort, {
            fileNo: (r) => r.fileNo,
            customerName: (r) => r.customer.name,
            daysOpen: (r) => r.daysOpen,
            gross: (r) => r.expenses.gross,
          })
        : [],
    [report, sort],
  );

  useEffect(() => {
    void api.getCustomerLookup({ active: 'true' }).then(setCustomers);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getShipmentOpenFilesReport({
          customerId: customerId ? Number(customerId) : undefined,
          port: port.trim() || undefined,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [customerId, port]);

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <ReportExportButtons
            disabled={!report || loading}
            onExcel={() => {
              if (!report) return;
              exportShipmentOpenFiles({ report, format: 'excel' });
            }}
            onPdf={() => {
              if (!report) return;
              exportShipmentOpenFiles({ report, format: 'pdf' });
            }}
          />
        }
      >
        <FilterField label={t().common.customer}>
          <Select
            value={customerId}
            size="sm"
            showClearButton={Boolean(customerId)}
            placeholder={labels.allCustomers}
            options={customers.map((c) => ({
              value: String(c.id),
              label: c.name,
            }))}
            onChange={(e) => setCustomerId(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.port}>
          <TextInput
            value={port}
            size="sm"
            showClearButton={Boolean(port)}
            onChange={(e) => setPort(e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>
      {error ? <Callout variant="danger">{error}</Callout> : null}
      {loading ? (
        <LoadingSpinner message={t().common.loading} />
      ) : !report || rows.length === 0 ? (
        <ListEmptyState title={t().common.empty} />
      ) : (
        <DataTablePanel className="min-h-0 flex-1">
          <table className="min-w-full text-left text-sm">
            <thead className={dataTableHeadClass}>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                <SortableTh sortKey="fileNo" sort={sort} onSort={toggleSort}>
                  {labels.shipmentFileNo}
                </SortableTh>
                <SortableTh sortKey="customerName" sort={sort} onSort={toggleSort}>
                  {t().common.customer}
                </SortableTh>
                <th className="px-4 py-3 font-semibold">{labels.port}</th>
                <th className="px-4 py-3 font-semibold">{t().common.date}</th>
                <SortableTh sortKey="daysOpen" sort={sort} onSort={toggleSort}>
                  {labels.daysOpen}
                </SortableTh>
                <th className="px-4 py-3 font-semibold">{labels.containers}</th>
                <SortableTh
                  sortKey="gross"
                  sort={sort}
                  onSort={toggleSort}
                  className="text-right"
                >
                  {labels.expenseGross}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-line/70">
                  <td className="px-4 py-3">
                    <Link
                      to={`/shipment-files/${row.id}`}
                      className="font-medium text-teal-700 hover:underline dark:text-teal-300"
                    >
                      {row.fileNo}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/customers/${row.customer.id}`} className="hover:underline">
                      {row.customer.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3">{row.port ?? '—'}</td>
                  <td className="px-4 py-3">{formatDate(row.effectiveDate)}</td>
                  <td className="px-4 py-3 tabular-nums">{row.daysOpen}</td>
                  <td className="px-4 py-3 tabular-nums">{row.containerCount}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatMoney(row.expenses.gross)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTablePanel>
      )}
    </PageShell>
  );
}
